// apps/vendor MFA enrollment-enforcement gate (ENG-340).
//
// A mandatory-MFA actor class must not reach any vendor surface without a
// verified MFA factor. The admin app enforces the equivalent invariant at
// mount time (apps/admin/src/auth-provider.tsx: "admin pages NEVER render
// without MFA"); this is the same idea for the vendor audience, but framed as
// an ENROLLMENT gate rather than a per-session step-up:
//
//   after sign-in, if the principal's actor class is MFA-mandatory and they
//   have NO verified factor at all, force enrollment before granting access.
//
// This deliberately does NOT touch the sign-in cutover flag path (MFA-M1) —
// it is a client-side leaf that reads the caller's factor state and blocks
// access when the mandatory class has zero factors. Pure + dependency-injected
// (`hasVerifiedFactor`) so it is unit-testable without a browser or network.
//
// The mandatory-class set mirrors the server policy DEFAULTS in
// services/auth-service/src/mfa/enforcement.ts (the `mandatory: true` classes:
// platform_staff, tenant_staff_manager, vendor). Keeping the mapping in one
// small, exhaustive function here means a new audience cannot silently slip
// past the gate — the switch fails to compile until it is classified.

import type { AuthAudience, Session } from "@clearpath/auth-client";

/**
 * Client-side mirror of the server ActorClass union
 * (services/auth-service/src/mfa/enforcement.ts). Only used to decide the
 * enrollment gate; the authoritative policy still lives server-side.
 */
export type ActorClass =
  | "platform_staff"
  | "tenant_staff_manager"
  | "tenant_staff_tech"
  | "portal_customer"
  | "vendor";

/**
 * Actor classes for which MFA is MANDATORY — a verified factor is required
 * before access is granted. Mirrors the `mandatory: true` DEFAULTS in
 * services/auth-service/src/mfa/enforcement.ts:
 *   - platform_staff        (Molaris staff)
 *   - tenant_staff_manager  (tenant staff, managers+)
 *   - vendor                (outsource / milling-center partners, ENG-294)
 *
 * `tenant_staff_tech` and `portal_customer` are optional/opt-in and are NOT
 * gated here.
 */
const MANDATORY_MFA_CLASSES: ReadonlySet<ActorClass> = new Set<ActorClass>([
  "platform_staff",
  "tenant_staff_manager",
  "vendor",
]);

/** True when the actor class must hold a verified MFA factor to be let in. */
export function isMandatoryMfaClass(actorClass: ActorClass): boolean {
  return MANDATORY_MFA_CLASSES.has(actorClass);
}

/**
 * Resolve the actor class from the token audience. Client-side mirror of
 * services/auth-service/src/mfa/enforcement.ts:resolveActorClassFromAudience
 * — the vendor bundle only ever runs under `clearpath-vendor`, but the full
 * mapping is kept so the gate is correct if this module is ever reused, and
 * so the switch is exhaustive (a new AuthAudience fails to compile here until
 * it is deliberately classified rather than defaulting past the gate).
 */
export function resolveActorClassFromAudience(audience: AuthAudience): ActorClass {
  switch (audience) {
    case "molaris-admin":
    case "molaris-account":
      return "platform_staff";
    case "clearpath-portal":
      return "portal_customer";
    case "clearpath-main":
    case "clearpath-light":
      // Both are internal tenant-staff audiences on the staff pool. The
      // manager/tech split is not threaded through the JWT client-side; the
      // server resolver defaults clearpath-main to tenant_staff_manager
      // (enforcement.ts) — mirror that here so the MANDATORY MFA floor is
      // applied, never skipped. clearpath-light (ENG-1046) is a narrow
      // storefront/intake surface running on the same staff pool; classifying
      // it under the mandatory floor is the fail-safe choice (its server-side
      // classification lands with the audience-registration follow-up).
      return "tenant_staff_manager";
    case "clearpath-vendor":
      return "vendor";
    default: {
      // AuthAudience is a closed union of the six literals above. This is a
      // compile-time exhaustiveness guard: adding a new audience without a
      // case fails to type-check rather than silently resolving to a
      // non-mandatory class and slipping past the gate.
      const unresolved: never = audience;
      throw new Error(`unhandled auth audience: ${String(unresolved)}`);
    }
  }
}

export type VendorMfaGateDecision =
  | { kind: "ready"; session: Session }
  | { kind: "enroll-required"; session: Session };

/**
 * Decide whether a signed-in principal may access the vendor app or must be
 * routed into MFA enrollment first.
 *
 *   - Non-mandatory class, or dev-bypass (`allowNoMfa`) → ready.
 *   - Session already carries a verified MFA claim → ready (a verified session
 *     self-evidently has a factor; no need to hit the factor-list endpoint).
 *   - Mandatory class, not verified → consult `hasVerifiedFactor()`:
 *       has a factor  → ready.
 *       no factor     → enroll-required.
 *   - `hasVerifiedFactor()` throws (network/token failure) → FAIL CLOSED into
 *     enroll-required: a mandatory-class principal we cannot prove holds a
 *     factor is never granted access.
 */
export async function resolveVendorMfaGate(input: {
  session: Session;
  actorClass: ActorClass;
  allowNoMfa: boolean;
  hasVerifiedFactor: () => Promise<boolean>;
}): Promise<VendorMfaGateDecision> {
  if (input.allowNoMfa || !isMandatoryMfaClass(input.actorClass)) {
    return { kind: "ready", session: input.session };
  }

  if (input.session.mfaVerified) {
    return { kind: "ready", session: input.session };
  }

  let hasFactor: boolean;
  try {
    hasFactor = await input.hasVerifiedFactor();
  } catch {
    // Fail closed — never let a mandatory-class principal in on an
    // indeterminate factor state.
    return { kind: "enroll-required", session: input.session };
  }

  return hasFactor
    ? { kind: "ready", session: input.session }
    : { kind: "enroll-required", session: input.session };
}
